/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useRef} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    List,
    ListItem,
    Heading,
    HStack,
    Text,
    Link as NavLink,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Link as RouteLink} from 'react-router-dom'
import Link from '../link'
import NavAssetCard from '../nav-asset-card'
import useNavigationHandler, {getNavigationProps} from '../../hooks/use-navigation-handler'
import useTagManager from '../../hooks/use-tag-manager'
import useMonetate from '../../hooks/use-monetate'
import {useSiteContext} from '../../contexts/site'
import LoadingSpinner from '../loading-spinner'

const LinksList = ({
    links = [],
    matchedAssets = {},
    totalColumns = 0,
    columnIndex = 0,
    allSameIndex = false,
    heading = '',
    variant,
    color,
    item,
    onLinkClick,
    headingLinkRef,
    ...otherProps
}) => {
    const tagManager = useTagManager()
    const {isYR} = useSiteContext()
    const monetate = useMonetate()
    const spinnerRef = useRef(null)
    const styles = useMultiStyleConfig('LinksList', {variant})
    const handleNavigation = useNavigationHandler({spinnerRef})

    const handleInternalNavigation = (event, href, track, isExternalRedirection = false) => {
        handleNavigation(event, getNavigationProps(href, isExternalRedirection), () => {
            onLinkClick?.()
            track?.()
        })
    }

    return (
        <>
            <Box ref={spinnerRef} className="hide-element">
                <LoadingSpinner position="fixed" top="auto" background="transparent" />
            </Box>
            <Box
                {...styles.container}
                {...(color ? {color: color} : {})}
                {...otherProps}
                className="links-list-box"
            >
                {heading &&
                    (heading.href ? (
                        <Link
                            to={heading.href}
                            className={`links-list-heading-link links-list-heading-link-${item?.id}`}
                            data-id={item?.id}
                            pointerEvents={links?.length ? 'none' : 'block'}
                            onClick={(event) => {
                                handleInternalNavigation(event, heading.href, () => {
                                    tagManager.navigationMain(
                                        'header',
                                        item?.parentCategoryTree?.[
                                            item?.parentCategoryTree?.length - 2
                                        ]?.name?.toLowerCase(),
                                        heading.text?.toLowerCase(),
                                        undefined
                                    )
                                })
                            }}
                            ref={headingLinkRef}
                            {...styles.headingLink}
                            display={item?.c_showInMenu ? 'block' : 'none'}
                        >
                            {/*** Desktop Category - L2 ***/}
                            <Heading
                                {...(heading.styles ? heading.styles : {})}
                                fontSize={links?.length ? '26px' : '14px'}
                                fontWeight={links?.length ? '400' : '300'}
                                whiteSpace="normal"
                                className="links-list-heading-ref"
                                fontFamily={
                                    links?.length ? (isYR ? 'caslon' : 'cormorant') : 'body'
                                }
                                color="black"
                                _hover={{
                                    color: links?.length ? 'black' : 'teal.400'
                                }}
                                marginBottom={links?.length ? '1rem' : '0.5rem'}
                            >
                                {heading.text}
                            </Heading>
                        </Link>
                    ) : (
                        <Heading
                            {...styles.heading}
                            className="links-list-heading-no-ref"
                            _hover={{
                                color: links?.length ? 'black' : 'teal.400'
                            }}
                            fontSize={links?.length ? '26px' : '14px'}
                            fontWeight={links?.length ? '400' : '300'}
                            fontFamily={links?.length ? 'caslon' : 'body'}
                            color="black"
                        >
                            {heading}
                        </Heading>
                    ))}

                {links && (
                    <List spacing={1} {...styles.list} className="links-stack-wrapper">
                        {variant === 'horizontal' ? (
                            <HStack className="links-stack">
                                {links
                                    ?.filter(({c_showInMenu}) => c_showInMenu)
                                    .map((link, i) => (
                                        <ListItem
                                            key={i}
                                            {...styles.listItem}
                                            sx={styles.listItemSx}
                                            className="list-item"
                                        >
                                            <Link
                                                to={link.href}
                                                className="list-item-link"
                                                onClick={(event) => {
                                                    handleInternalNavigation(event, link.href)
                                                }}
                                                {...(link.styles ? link.styles : {})}
                                                {...styles.list.link}
                                            >
                                                {link.text}
                                            </Link>
                                        </ListItem>
                                    ))}
                            </HStack>
                        ) : (
                            links
                                ?.filter(({c_showInMenu}) => c_showInMenu)
                                ?.map((link, i) => {
                                    const redirectionProps =
                                        link.href?.indexOf('.html') === -1 &&
                                        !link?.c_isExternalRedirection
                                            ? {to: link.href, isExternal: false, as: RouteLink}
                                            : {to: link.href, isExternal: true, as: NavLink}
                                    return (
                                        <ListItem
                                            key={i}
                                            className={`links-list-item links-list-item-${link?.id}`}
                                            data-id={link?.id}
                                            id={`cat-${link?.id}`}
                                        >
                                            <Link
                                                onClick={(event) => {
                                                    handleInternalNavigation(
                                                        event,
                                                        link.href,
                                                        () => {
                                                            tagManager.navigationMain(
                                                                'header',
                                                                item?.parentCategoryTree?.[
                                                                    item?.parentCategoryTree
                                                                        ?.length - 2
                                                                ]?.name?.toLowerCase(),
                                                                link?.text?.toLowerCase(),
                                                                item?.name?.toLowerCase()
                                                            )
                                                            monetate.setCategoriesViewed(link?.text)
                                                            monetate.setNavigationClicks(link?.text)
                                                        },
                                                        link?.c_isExternalRedirection
                                                    )
                                                }}
                                                {...(link.styles ? link.styles : {})}
                                                {...styles.list.link}
                                                {...redirectionProps}
                                                whiteSpace="normal"
                                                position="relative"
                                                width="fit-content"
                                                display="block"
                                                marginBottom="7px"
                                                className={`link-item link-item-${link?.id}`}
                                            >
                                                <Box
                                                    display="flex"
                                                    flexDirection="column"
                                                    className={`link-item-box link-item-box-${link?.id}`}
                                                    alignItems="flex-start"
                                                >
                                                    {/*** Desktop Category - L3 ***/}
                                                    <Box
                                                        display="flex"
                                                        alignItems="flex-start"
                                                        className={`link-item-box-l3 link-item-box-l3-${link?.id}`}
                                                        gap="1"
                                                    >
                                                        <Text
                                                            fontSize="14px"
                                                            lineHeight="22px"
                                                            fontWeight="400"
                                                            className={`link-item-box-l3-text link-item-box-l3-text-${link?.id}`}
                                                            whiteSpace="nowrap"
                                                            overflow="hidden"
                                                            textOverflow="ellipsis"
                                                            maxWidth={{lg: '160px', xl: '250px'}}
                                                        >
                                                            {link.text}
                                                        </Text>
                                                        {link?.c_badgeText && (
                                                            <Text
                                                                fontSize="8px"
                                                                fontWeight="600"
                                                                className={`link-item-box-badge link-item-box-badge-${link?.id}`}
                                                                color={
                                                                    link?.c_badgeTextColor ||
                                                                    'black.100'
                                                                }
                                                                mt="1px"
                                                            >
                                                                {link.c_badgeText}
                                                            </Text>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </Link>
                                        </ListItem>
                                    )
                                })
                        )}
                    </List>
                )}
            </Box>
            {matchedAssets?.length ? (
                <Box
                    display="inline-flex"
                    height="auto"
                    gap="10px"
                    marginLeft={allSameIndex && columnIndex === totalColumns - 1 ? 'auto' : 'unset'}
                    className="matched-assets-box"
                >
                    {matchedAssets.map((asset) => (
                        <NavAssetCard
                            key={asset.id}
                            asset={asset}
                            onClickTrack={() =>
                                tagManager.navigationCategoryCard(
                                    item?.name?.toLowerCase(),
                                    asset.ctaText || asset.altText,
                                    undefined
                                )
                            }
                        />
                    ))}
                </Box>
            ) : null}
        </>
    )
}

LinksList.propTypes = {
    links: PropTypes.arrayOf(
        PropTypes.shape({
            href: PropTypes.string,
            text: PropTypes.string
        })
    ).isRequired,
    totalColumns: PropTypes.number,
    columnIndex: PropTypes.number,
    allSameIndex: PropTypes.bool,
    matchedAssets: PropTypes.object,
    heading: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    variant: PropTypes.oneOf(['vertical', 'horizontal']),
    color: PropTypes.string,
    onLinkClick: PropTypes.func,
    item: PropTypes.object,
    headingLinkRef: PropTypes.object
}

export default LinksList
